'use client';

import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { AhafoodLogo } from '@ahamove/icons/order';
import { cn } from '@ahm/common-helpers';
import { ChevronDown, MapPin, Search, ShoppingBag } from 'lucide-react';
import { useParams, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import UserButton from './user/user-button';
import { useOrderListSheet } from '@/app/[locale]/order-list/order-list-sheet-context';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { CITIES, useAiFoodOrderStore } from '@/store';

export default function DesktopHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAmori, setShowAmori] = useState(true);
  const params = useParams();
  const router = useRouter();
  const routes = useCityRoutes();
  const address = useAiFoodOrderStore.use.address();
  const citySlug = Array.isArray(params.city) ? params.city[0] : params.city;
  const cityData = CITIES.find((c) => c.slug === citySlug);
  const cityLabel = cityData?.label ?? citySlug;
  const { openCitySheet } = useStoreCitySheet();
  const { openSheet } = useOrderListSheet();

  // Track scroll for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Toggle between Amori and AhaFood logo
  useEffect(() => {
    const interval = setInterval(() => {
      setShowAmori((prev) => !prev);
    }, 3000); // Switch every 3 seconds
    return () => clearInterval(interval);
  }, []);

  const handleAmoriClick = () => {
    openSheet();
  };

  const handleSearch = () => {
    router.push(routes.search);
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-50 hidden md:block border-b border-neutral-10 bg-white/95 backdrop-blur-md transition-shadow duration-300',
        isScrolled && 'shadow-[0_6px_20px_-10px_rgba(0,0,0,0.18)]'
      )}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 xl:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20 gap-2 lg:gap-6">
          {/* Logo */}
          <div className="flex min-w-0 items-center gap-2 lg:gap-3">
            <button
              onClick={() => router.push(routes.home)}
              className="relative flex shrink-0 items-center gap-2 hover:opacity-80 transition-opacity md:w-32 h-10 md:h-12"
            >
              {/* Amori */}
              <div
                className={cn(
                  'absolute inset-0 flex items-center gap-2 transition-opacity duration-700',
                  showAmori ? 'opacity-100' : 'opacity-0 pointer-events-none'
                )}
              >
                <div className="relative size-8 lg:size-10">
                  <Image
                    src="/static/images/amori-nav-avt.webp"
                    alt="Amori"
                    fill
                    className="object-contain"
                  />
                </div>
                <span className="text-xl lg:text-2xl font-bold bg-gradient-to-r from-[#305B8D] via-[#F879FD] to-primary-50 text-transparent bg-clip-text whitespace-nowrap">
                  Amori
                </span>
              </div>

              {/* AhaFood */}
              <div
                className={cn(
                  'absolute inset-0 flex items-center transition-opacity duration-700',
                  !showAmori ? 'opacity-100' : 'opacity-0 pointer-events-none'
                )}
              >
                <AhafoodLogo className="w-36 h-10" />
              </div>
            </button>

            {/* Location Selector */}
            <Button
              variant="ghost"
              onClick={openCitySheet}
              className="flex min-w-0 items-center gap-2 px-2 text-neutral-90 hover:text-neutral-90 hover:bg-white/20"
            >
              <MapPin className="size-4 shrink-0 text-primary-50" />
              <div className="flex min-w-0 flex-col items-start">
                <span className="text-xs text-neutral-50">Giao món đến</span>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-semibold line-clamp-1 max-w-[110px] lg:max-w-[200px]">
                    {address?.description ?? cityLabel}
                  </span>
                  <ChevronDown className="size-3 shrink-0" />
                </div>
              </div>
            </Button>
          </div>

          {/* Search Bar */}
          <div className="flex-1 min-w-0 max-w-2xl">
            <button
              onClick={handleSearch}
              className="w-full flex items-center gap-2 px-3 py-2.5 bg-white/80 hover:bg-white rounded-full transition-colors group backdrop-blur-sm"
            >
              <Search className="size-5 shrink-0 text-neutral-50 group-hover:text-neutral-70 transition-colors" />
              <span className="truncate whitespace-nowrap text-sm text-neutral-50">
                Tìm tên món, tên quán
              </span>
            </button>
          </div>

          {/* Right Actions */}
          <div className="flex shrink-0 items-center gap-2 lg:gap-5">
            {/* Amori AI Button */}
            {/* <Button
              onClick={handleAmoriClick}
              className="bg-gradient-to-r from-[#305B8D] via-[#F879FD] to-primary-50 hover:opacity-90 text-white px-4 py-2 h-auto"
            >
              <div className="flex items-center gap-2">
                <div className="size-6 relative overflow-hidden rounded-sm">
                  <Image
                    src="/static/images/amori-nav-avt.webp"
                    alt="Amori AI"
                    fill
                    className="object-cover"
                  />
                </div>
                <span className="font-semibold">Hỏi Amori</span>
              </div>
            </Button> */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAmoriClick();
              }}
              className="flex shrink-0 items-center gap-2 whitespace-nowrap font-semibold text-neutral-80 underline-offset-4 transition-colors hover:text-primary hover:underline"
            >
              <ShoppingBag className="size-4 shrink-0" />
              Đơn hàng của tôi
            </button>

            {/* User Button */}
            <UserButton />
          </div>
        </div>

        {/* Quick Navigation Tabs */}
        {/* <div className="flex items-center gap-6 py-2 border-t border-white/10">
          <button
            onClick={() => router.push(routes.home)}
            className="text-sm font-medium text-neutral-90 hover:text-primary-50 transition-colors pb-2 border-b-2 border-transparent hover:border-primary-50"
          >
            Quán ăn
          </button>
          <button
            onClick={() => router.push(routes.solomeal)}
            className="text-sm font-medium text-neutral-90 hover:text-primary-50 transition-colors pb-2 border-b-2 border-transparent hover:border-primary-50"
          >
            SoloMeal
          </button>
          <button
            onClick={() => router.push('/order-list')}
            className="text-sm font-medium text-neutral-90 hover:text-primary-50 transition-colors pb-2 border-b-2 border-transparent hover:border-primary-50"
          >
            Đơn hàng
          </button>
        </div> */}
      </div>
    </header>
  );
}
